import { useState } from "react";
import { PieChart, Pie, Cell, ResponsiveContainer } from "recharts";
import { Chapter, Reveal, Btn } from "../shared";
import { waLink } from "../../data/content";
import { WhatsAppIcon } from "../Navbar";

const SLICES = [
  { name: "Accommodation & Stays", pct: 50, color: "#0C3B2E" },
  { name: "Decor", pct: 35, color: "#C5A059" },
  { name: "Artist", pct: 15, color: "#E8D3A2" },
];

const fmt = (v) => (v >= 100 ? `₹${(v / 100).toFixed(2).replace(/\.?0+$/, "")} Cr` : `₹${v} Lakh`);

export default function BudgetEstimator() {
  const [lakh, setLakh] = useState(50);
  return (
    <section className="bg-ivory py-24 md:py-32" data-testid="budget-estimator">
      <div className="mx-auto max-w-7xl px-6 md:px-12">
        <Chapter num="04" title="Plan Your Wedding Budget" />
        <div className="mt-6 grid items-center gap-16 lg:grid-cols-2">
          <div>
            <Reveal delay={0.1}>
              <h2 className="font-serif text-4xl md:text-6xl leading-[1.05] text-emerald">
                Where does a wedding <span className="italic text-gold-text">budget</span> go?
              </h2>
            </Reveal>
            <Reveal delay={0.2}>
              <p className="mt-6 max-w-lg text-sm md:text-base leading-relaxed text-stone2">
                Move the slider to your approximate wedding budget and see a sample allocation of how The Big Wedd would plan it across your celebration.
              </p>
            </Reveal>

            <Reveal delay={0.3}>
              <div className="mt-10">
                <p className="font-serif text-5xl md:text-7xl text-emerald" data-testid="budget-total">{fmt(lakh)}</p>
                <input
                  type="range"
                  min="10"
                  max="500"
                  step="5"
                  value={lakh}
                  onChange={(e) => setLakh(Number(e.target.value))}
                  data-testid="budget-slider"
                  aria-label="Wedding budget in lakhs"
                  className="mt-8 w-full cursor-pointer"
                />
                <div className="mt-3 flex justify-between text-[10px] uppercase tracking-[0.2em] text-stone2/60">
                  <span>₹10 Lakh</span><span>₹5 Crore</span>
                </div>
                <div className="mt-6 flex flex-wrap gap-3">
                  {[25, 50, 100, 200].map((v) => (
                    <button
                      key={v}
                      onClick={() => setLakh(v)}
                      data-testid={`budget-chip-${v}`}
                      className={`rounded-full border px-5 py-2.5 text-[11px] uppercase tracking-[0.18em] transition-all duration-300 ${lakh === v ? "border-emerald bg-emerald text-ivory" : "border-gold/40 text-stone2 hover:border-gold hover:text-gold-text"}`}
                    >
                      {fmt(v)}
                    </button>
                  ))}
                </div>
              </div>
            </Reveal>
          </div>

          <div>
            <Reveal delay={0.2}>
              <div className="relative h-[300px] md:h-[360px]" data-testid="budget-donut">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie data={SLICES} dataKey="pct" innerRadius="66%" outerRadius="96%" startAngle={90} endAngle={-270} paddingAngle={2} stroke="none">
                      {SLICES.map((s) => <Cell key={s.name} fill={s.color} />)}
                    </Pie>
                  </PieChart>
                </ResponsiveContainer>
                <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center">
                  <span className="text-[10px] uppercase tracking-[0.3em] text-stone2">Total</span>
                  <span className="font-serif text-3xl md:text-4xl text-emerald">100%</span>
                </div>
              </div>
            </Reveal>
            <Reveal delay={0.3}>
              <ul className="mt-8 space-y-4">
                {SLICES.map((s) => (
                  <li key={s.name} className="flex items-center justify-between gap-4 border-b border-gold/20 pb-4" data-testid={`budget-row-${s.pct}`}>
                    <span className="flex items-center gap-3 text-sm text-stone2">
                      <span className="h-3 w-3 rounded-sm" style={{ background: s.color }} />
                      {s.name}
                    </span>
                    <span className="text-right">
                      <span className="font-serif text-xl md:text-2xl text-emerald">{s.pct}%</span>
                      <span className="ml-3 text-sm text-gold-text">{fmt(Math.round((lakh * s.pct) / 100))}</span>
                    </span>
                  </li>
                ))}
                <li className="flex items-center justify-between pt-2">
                  <span className="text-xs font-medium uppercase tracking-[0.25em] text-emerald">Total</span>
                  <span className="font-serif text-2xl text-emerald">{fmt(lakh)}</span>
                </li>
              </ul>
            </Reveal>
          </div>
        </div>

        <Reveal delay={0.2}>
          <p className="mx-auto mt-14 max-w-3xl text-center text-xs md:text-sm italic leading-relaxed text-stone2/80">
            “This is an illustrative budget allocation. Your actual wedding budget can be customised based on your destination, guest count, venue, wedding style and priorities.”
          </p>
          <div className="mt-8 flex flex-col items-center justify-center gap-4 sm:flex-row">
            <Btn to="/enquire" testid="budget-cta-proposal">Get My Custom Wedding Proposal</Btn>
            <a href={waLink()} target="_blank" rel="noopener noreferrer" data-testid="budget-cta-whatsapp" className="inline-flex items-center gap-3 rounded-full border border-gold/60 px-8 py-4 text-[11px] md:text-xs font-medium uppercase tracking-[0.22em] text-emerald transition-all duration-500 hover:bg-gold hover:text-ink">
              <WhatsAppIcon className="h-4 w-4" /> Chat on WhatsApp
            </a>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
